import { HTMLAttributes, ReactNode } from 'react'
import { Check, CircleSlash, Eye, Trash2 } from 'lucide-react'
import { TFunction } from 'i18next'
import { useSortable } from '@dnd-kit/sortable'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { v4 as uuid } from 'uuid'
import {
  RaidPlanAssignmentEntity,
  RaidPlanEncounterAssignmentAbility,
  RaidPlanEncounterAssignmentAbilityKey,
} from 'app/frontend/types/custom/RaidPlan'
import TeamPlanAssignmentScopeCard from './TeamPlanAssignmentScopeCard'
import {
  AssignmentViewMode,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentGroups,
  getEffectiveAssignmentViewMode,
  getAssignmentScopes,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from './assignmentTimeline'
import { getEncounterAbilityShowByDefault } from 'app/frontend/types/custom/EncounterAbilityAssignmentDefaults'
import { getAssignmentEntityLabel } from './assignmentEntityLabels'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'
import { getDuplicateIdentitiesByScopeId } from './assignmentDuplicateIdentities'
import SpellIcon from './SpellIcon'
import TeamPlanAssignmentDialog from './TeamPlanAssignmentDialog'
import { formatTimelineTimestamp } from './timelineMath'
import { cn } from '@/lib/utils'
import { EncounterAbilityRow } from '../hooks/useTeamPlanComputedData'

type Scope = {
  id: string
  label: string
  castIndexes: number[]
  castGroups: number[][]
}

type PaletteEntity = {
  id: string
  entity: RaidPlanAssignmentEntity
  label: ReactNode
  category:
    | 'collection'
    | 'group'
    | 'specific_character'
    | 'by_role'
    | 'by_class'
}

const extractUuid = (value: string) => {
  const match = value.match(
    /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i,
  )
  return match?.[0] ?? null
}

const raidPlanBaseUrl = 'https://raidstrats.gg/planner?view='
const isStandardAbilityKey = (abilityKey: string) => /^\d+$/.test(abilityKey)

type Props = {
  row: EncounterAbilityRow
  t: TFunction
  canEdit: boolean
  openRaidPlanAbilityKey: string | null
  setOpenRaidPlanAbilityKey: (abilityKey: string | null) => void
  setConfirmRemoveAbilityKey: (abilityKey: string | null) => void
  setAssignmentDialogOpen: (open: boolean) => void
  paletteEntities: PaletteEntity[]
  paletteLabelByIdentity: Map<string, ReactNode>
  memberInfoByCharacterId: Record<number, { name: string; className: string }>
  specializationById: Record<
    number,
    {
      name: string
      icon: string
      classId: number
      className: string
    }
  >
  storedAbility?: RaidPlanEncounterAssignmentAbility
  updateAbility: (
    abilityKey: RaidPlanEncounterAssignmentAbilityKey,
    spellId: number,
    castCount: number,
    assignmentGroups: number,
    updater: (ability: RaidPlanEncounterAssignmentAbility) => void,
    pushHistory?: boolean,
  ) => void
  onCommit: () => void
  removeAbility: (abilityKey: RaidPlanEncounterAssignmentAbilityKey) => void
}

const TeamPlanAssignmentRow = ({
  row,
  t,
  canEdit,
  openRaidPlanAbilityKey,
  setOpenRaidPlanAbilityKey,
  setConfirmRemoveAbilityKey,
  setAssignmentDialogOpen,
  paletteEntities,
  paletteLabelByIdentity,
  memberInfoByCharacterId,
  specializationById,
  storedAbility,
  updateAbility,
  onCommit,
  removeAbility,
}: Props) => {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: row.abilityKey,
    disabled: !canEdit,
  })
  const isCustomAbility = !isStandardAbilityKey(row.abilityKey)
  const isShownByDefault =
    !isCustomAbility && getEncounterAbilityShowByDefault(row.spellId)
  const normalizedAssignmentGroups = getEffectiveAssignmentGroups(
    row.assignmentGroups,
    storedAbility?.assignment_groups,
  )
  const abilityAssignment: RaidPlanEncounterAssignmentAbility = {
    spell_id: row.spellId,
    casts: Array.from({ length: row.entries.length }, (_, castIndex) =>
      normalizeAssignmentCast(
        storedAbility?.casts?.[castIndex],
        normalizedAssignmentGroups,
      ),
    ),
    assignment_groups: storedAbility?.assignment_groups,
    title: storedAbility?.title,
    message: storedAbility?.message,
    tts_message: storedAbility?.tts_message,
    cast_overrides: storedAbility?.cast_overrides,
    rgb: storedAbility?.rgb,
    raid_plan_link: storedAbility?.raid_plan_link,
    view_mode: storedAbility?.view_mode,
    custom_grouping: getEffectiveAbilityCustomGrouping(
      storedAbility?.spell_id ?? row.spellId,
      storedAbility?.custom_grouping,
    ),
  }
  const effectiveAssignmentGroups = getEffectiveAssignmentGroups(
    row.assignmentGroups,
    abilityAssignment.assignment_groups,
  )
  const abilityViewMode: AssignmentViewMode = getEffectiveAssignmentViewMode(
    abilityAssignment.view_mode,
  )
  const abilityCustomGrouping = getEffectiveAbilityCustomGrouping(
    abilityAssignment.spell_id,
    abilityAssignment.custom_grouping,
  )
  const scopes: Scope[] = getAssignmentScopes(
    row.entries,
    abilityViewMode,
    t,
    abilityCustomGrouping,
  )
  const duplicateIdentitiesByScopeId = getDuplicateIdentitiesByScopeId({
    ability: abilityAssignment,
    scopes,
    assignmentGroups: effectiveAssignmentGroups,
    viewMode: abilityViewMode,
  })
  const assignmentUsageCountByIdentity = scopes.reduce<Record<string, number>>(
    (acc, scope) => {
      const scopeCountByIdentity = scope.castIndexes.reduce<
        Record<string, number>
      >((scopeAcc, castIndex) => {
        const cast = normalizeAssignmentCast(
          abilityAssignment.casts[castIndex],
          effectiveAssignmentGroups,
        )
        const castCountByIdentity = cast.boxes.reduce<Record<string, number>>(
          (castAcc, box) => {
            normalizeAssignmentBox(box).entities.forEach((entity) => {
              const identity = getAssignmentEntityIdentity(entity)
              castAcc[identity] = (castAcc[identity] ?? 0) + 1
            })
            return castAcc
          },
          {},
        )
        Object.entries(castCountByIdentity).forEach(([identity, count]) => {
          scopeAcc[identity] = Math.max(scopeAcc[identity] ?? 0, count)
        })
        return scopeAcc
      }, {})
      Object.entries(scopeCountByIdentity).forEach(([identity, count]) => {
        acc[identity] = (acc[identity] ?? 0) + count
      })
      return acc
    },
    {},
  )
  const raidPlanValue = (abilityAssignment.raid_plan_link ?? '').trim()
  const extractedRaidPlanId = extractUuid(raidPlanValue)
  const raidPlanId = extractedRaidPlanId ?? raidPlanValue
  const raidPlanUrl = `${raidPlanBaseUrl}${raidPlanId}`
  const hasRaidPlanId = Boolean(raidPlanId)
  const hasValidRaidPlanId = Boolean(extractedRaidPlanId)
  const hasAssignedEntities = abilityAssignment.casts.some((cast) =>
    normalizeAssignmentCast(cast, effectiveAssignmentGroups).boxes.some(
      (box) => normalizeAssignmentBox(box).entities.length > 0,
    ),
  )
  const iconInteractionProps = canEdit
    ? ({ ...attributes, ...listeners } as HTMLAttributes<HTMLSpanElement>)
    : undefined

  return (
    <div
      ref={setNodeRef}
      className='flex flex-col gap-2 mb-8'
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition,
        opacity: isDragging ? 0.8 : 1,
      }}
    >
      <div className='flex items-center justify-between gap-2'>
        {isCustomAbility ? (
          <div className='flex items-center gap-2 text-xl min-w-0'>
            <span
              ref={setActivatorNodeRef}
              {...iconInteractionProps}
              className={cn(
                'inline-flex',
                canEdit ? 'cursor-grab active:cursor-grabbing' : undefined,
              )}
            >
              <SpellIcon
                spellId={136243}
                className='w-8 h-8 rounded border border-border'
              />
            </span>
            <span className='truncate'>
              {abilityAssignment.title ?? row.name}
            </span>
          </div>
        ) : (
          <div className='flex items-center gap-2 text-xl min-w-0'>
            <span
              ref={setActivatorNodeRef}
              {...iconInteractionProps}
              className={cn(
                'inline-flex',
                canEdit ? 'cursor-grab active:cursor-grabbing' : undefined,
              )}
            >
              <SpellIcon
                spellId={row.spellId}
                className='w-8 h-8 rounded border border-border'
              />
            </span>
            <a
              href={`https://wowhead.com/spell=${row.spellId}`}
              target='_blank'
              rel='noreferrer'
              className='truncate min-w-0'
              style={{ color: row.color }}
            >
              {abilityAssignment.title ?? row.name}
            </a>
          </div>
        )}
        {canEdit ? (
          <div className='flex items-center gap-2'>
            <Tabs
              value={abilityViewMode}
              onValueChange={(value) =>
                updateAbility(
                  row.abilityKey,
                  row.spellId,
                  row.entries.length,
                  effectiveAssignmentGroups,
                  (ability) => {
                    ability.view_mode = value as AssignmentViewMode
                  },
                  true,
                )
              }
            >
              <TabsList>
                <TabsTrigger value='all_together'>
                  {t('dashboard.team.plans.assignments.view-modes.all')}
                </TabsTrigger>
                <TabsTrigger value='alternating'>
                  {t('dashboard.team.plans.assignments.view-modes.alternating')}
                </TabsTrigger>
                <TabsTrigger value='all_separate'>
                  {t('dashboard.team.plans.assignments.view-modes.separate')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <TeamPlanAssignmentDialog
              open={openRaidPlanAbilityKey === row.abilityKey}
              onOpenChange={(open) =>
                setOpenRaidPlanAbilityKey(open ? row.abilityKey : null)
              }
              row={row}
              ability={abilityAssignment}
              spellName={abilityAssignment.title ?? row.name}
              defaultTitle={
                isCustomAbility
                  ? t('dashboard.team.plans.assignments.custom')
                  : row.name
              }
              canEdit={canEdit}
              assignmentGroups={effectiveAssignmentGroups}
              onUpdate={(updater, pushHistory) =>
                updateAbility(
                  row.abilityKey,
                  row.spellId,
                  row.entries.length,
                  effectiveAssignmentGroups,
                  updater,
                  pushHistory,
                )
              }
              onCommit={onCommit}
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  className={cn(
                    'min-w-20 justify-between gap-2',
                    hasValidRaidPlanId ? 'border-primary' : '',
                  )}
                >
                  <span>
                    {t('dashboard.team.plans.assignments.raid-plan-link')}
                  </span>
                  <span className='inline-flex h-4 w-4 items-center justify-center'>
                    {hasValidRaidPlanId ? (
                      <Check className='h-4 w-4 text-primary' />
                    ) : (
                      <CircleSlash className='h-4 w-4 text-muted-foreground' />
                    )}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-96 space-y-2'>
                <div className='flex items-center gap-2'>
                  <Input
                    value={abilityAssignment.raid_plan_link ?? ''}
                    onChange={(event) =>
                      updateAbility(
                        row.abilityKey,
                        row.spellId,
                        row.entries.length,
                        effectiveAssignmentGroups,
                        (ability) => {
                          ability.raid_plan_link =
                            event.target.value || undefined
                        },
                        false,
                      )
                    }
                    onPaste={(event) => {
                      const pasted = event.clipboardData.getData('text')
                      const extractedUuid = extractUuid(pasted)
                      if (!extractedUuid) return
                      event.preventDefault()
                      updateAbility(
                        row.abilityKey,
                        row.spellId,
                        row.entries.length,
                        effectiveAssignmentGroups,
                        (ability) => {
                          ability.raid_plan_link = extractedUuid
                        },
                        false,
                      )
                    }}
                    onBlur={onCommit}
                    placeholder={t(
                      'dashboard.team.plans.assignments.raid-plan-link-placeholder',
                    )}
                    className='h-8 text-xs'
                    disabled={!canEdit}
                  />
                  {hasRaidPlanId ? (
                    <a
                      href={raidPlanUrl}
                      target='_blank'
                      rel='noreferrer'
                      className={cn(
                        buttonVariants({
                          variant: 'outline',
                          size: 'icon',
                        }),
                        'h-8 w-8 shrink-0',
                      )}
                    >
                      <Eye className='h-4 w-4' />
                    </a>
                  ) : (
                    <span
                      className={cn(
                        buttonVariants({
                          variant: 'outline',
                          size: 'icon',
                        }),
                        'h-8 w-8 shrink-0 pointer-events-none opacity-50',
                      )}
                    >
                      <Eye className='h-4 w-4' />
                    </span>
                  )}
                  <Button
                    type='button'
                    variant='outline'
                    size='icon'
                    className='h-8 w-8 shrink-0'
                    onClick={() => {
                      updateAbility(
                        row.abilityKey,
                        row.spellId,
                        row.entries.length,
                        effectiveAssignmentGroups,
                        (ability) => {
                          ability.raid_plan_link = undefined
                        },
                        true,
                      )
                      setOpenRaidPlanAbilityKey(null)
                    }}
                    disabled={
                      !canEdit ||
                      !(abilityAssignment.raid_plan_link ?? '').trim()
                    }
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            <Button
              type='button'
              variant='outline'
              disabled={isShownByDefault}
              onClick={() => {
                if (isShownByDefault) return
                if (!hasAssignedEntities) {
                  removeAbility(row.abilityKey)
                  return
                }
                setConfirmRemoveAbilityKey(row.abilityKey)
              }}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </div>
        ) : null}
      </div>

      {scopes.length === 0 ? (
        <div className='flex items-center gap-1 text-sm text-muted-foreground'>
          <span>This ability has no casts.</span>
          {canEdit ? (
            <>
              {' '}
              <span>Add casts in the </span>
              <Button
                type='button'
                variant='outline'
                className='px-1.5'
                size='xs'
                onClick={() => setOpenRaidPlanAbilityKey(row.abilityKey)}
              >
                settings
              </Button>
            </>
          ) : null}
        </div>
      ) : null}

      {scopes.map((scope) => {
        const representativeCast = normalizeAssignmentCast(
          abilityAssignment.casts[scope.castIndexes[0]],
          effectiveAssignmentGroups,
        )
        const scopeBoxes = Array.from(
          { length: effectiveAssignmentGroups },
          (_, boxIndex) =>
            normalizeAssignmentBox(representativeCast.boxes[boxIndex]),
        )
        const duplicateIdentities =
          duplicateIdentitiesByScopeId[scope.id] ?? new Set<string>()
        const scopeEntitiesByGroup = scopeBoxes.map((box) => box.entities)
        const raidPlanPlayers = scopeEntitiesByGroup
          .flat()
          .flatMap((entity) => {
            if (entity.kind !== 'member') {
              return []
            }
            const memberInfo = memberInfoByCharacterId[entity.character_id]
            const specialization = entity.spec_id
              ? specializationById[entity.spec_id]
              : undefined
            const className = specialization?.className ?? memberInfo?.className
            const name = memberInfo?.name ?? entity.label
            if (!className || !name) {
              return []
            }
            return [{ name, className }]
          })
        return (
          <TeamPlanAssignmentScopeCard
            key={scope.id}
            scope={scope}
            spellId={row.spellId}
            castTimestamps={scope.castGroups.map((castGroup) =>
              castGroup
                .map((castIndex) =>
                  formatTimelineTimestamp(row.entries[castIndex]?.time ?? 0),
                )
                .join(' / '),
            )}
            entitiesByGroup={scopeEntitiesByGroup}
            assignmentGroups={effectiveAssignmentGroups}
            canEdit={canEdit}
            paletteEntities={paletteEntities}
            assignmentUsageCountByIdentity={assignmentUsageCountByIdentity}
            onSave={(nextEntitiesByGroup) =>
              updateAbility(
                row.abilityKey,
                row.spellId,
                row.entries.length,
                effectiveAssignmentGroups,
                (ability) => {
                  scope.castIndexes.forEach((castIndex) => {
                    const cast = normalizeAssignmentCast(
                      ability.casts[castIndex],
                      effectiveAssignmentGroups,
                    )
                    nextEntitiesByGroup.forEach((nextEntities, boxIndex) => {
                      cast.boxes[boxIndex] = {
                        ...normalizeAssignmentBox(cast.boxes[boxIndex]),
                        entities: nextEntities.map((entity) => ({
                          ...entity,
                          id: entity.id ?? uuid(),
                        })),
                      }
                    })
                    ability.casts[castIndex] = cast
                  })
                },
                true,
              )
            }
            assignLabel={t('dashboard.team.plans.assignments.assign')}
            raidPlanId={hasValidRaidPlanId ? extractedRaidPlanId : null}
            raidPlanPlayers={raidPlanPlayers}
            abilityViewMode={abilityViewMode}
            duplicateIdentities={duplicateIdentities}
            getEntityLabel={(entity) =>
              paletteLabelByIdentity.get(getAssignmentEntityIdentity(entity)) ??
              getAssignmentEntityLabel(t, entity)
            }
            groupLabel={(count) =>
              t('dashboard.team.plans.assignments.group-label', { count })
            }
            onAssignmentDialogOpenChange={setAssignmentDialogOpen}
          />
        )
      })}
    </div>
  )
}

export default TeamPlanAssignmentRow
